// API服务层 - 统一管理所有API调用
export interface ApiResponse<T = unknown> {
  res_status_code: string;
  res_message: string;
  res_content: T;
  exception_message?: string;
}

export interface FieldConfig {
  id: number;
  function_method: string;
  function_name: string | null;
  create_time: string;
  create_user_id: number;
  update_user_id: number;
  update_time: string;
  default_field_list: unknown[] | null;
  field_list: FieldItem[];
}

export interface FieldItem {
  field_name: string;
  display_name: string;
  field_type: string;
  is_visible: boolean;
}

export interface SearchToolResponse {
  field_conf: FieldConfig;
  total_pages: number;
  page_index: number;
  total_count: number;
  data_list: Record<string, unknown>[];
  sum_data: unknown[] | null;
  columns: unknown[] | null;
  group_name: string | null;
  tips: string | null;
  specific_field_result: unknown | null;
  exception_message: string | null;
}

export interface SearchToolParams {
  page_size?: number;
  page_no?: number;
  search_id: string;
  order?: string;
}

// API配置
const API_CONFIG = {
  baseURL: 'https://gate.shjinjia.com.cn',
  endpoints: {
    searchTool: '/api/SearchTool/ExecuteSearchToolInstance'
  }
};

const token = 'eyJhbGciOiJSUzI1NiIsImtpZCI6IjdFNjVCNjdDQkY0OUI3QTE1M0E4NjUzQUMyRTMyQ0E1MjVGQ0M5RjhSUzI1NiIsInR5cCI6ImF0K2p3dCIsIng1dCI6ImZtVzJmTDlKdDZGVHFHVTZ3dU1zcFNYOHlmZyJ9.eyJuYmYiOjE3NTM0MzcyMjIsImV4cCI6MTc1MzQ0NDQyMiwiaXNzIjoiaHR0cHM6Ly9nYXRlLnNoamluamlhLmNvbS5jbiIsImNsaWVudF9pZCI6ImpzX29ha192dWUiLCJzdWIiOiJjN2QxZmU2MWNiNzY0Mzk3ODQxZjFjMGUzMWY2Mjk5YyIsImF1dGhfdGltZSI6MTc1MjE5OTA3MywiaWRwIjoibG9jYWwiLCJuYW1lIjoi5Y-y57uS57uSIiwibGlkIjoi5Y-y57uS57uSIiwiaWV1IjoiMCIsImlidSI6IjAiLCJpZmwiOiIwIiwianRpIjoiOUE0NjM5MTU4REE2MDQ1QTU2RjExMkExOTJENUFFNUIiLCJzaWQiOiI0RTYzMjM0ODFGNjE0MDgxQjhCRjJCQTZFNDI5MTgyQSIsImlhdCI6MTc1MzA3OTkwNSwic2NvcGUiOlsiYXBpIiwib3BlbmlkIiwicHJvZmlsZSIsIm9mZmxpbmVfYWNjZXNzIl0sImFtciI6WyJwd2QiXX0.sBkC3GUBS8i85xLuNjA2Fbs6S-i3MNoBXL_hPBKH9bX9z6tdtO0g6TyHRsnwn1Dv8r0lUwi1PpbjI6l8QO0AqjaRhNJTqtnAWf7hSo7mTGsI-jpo3I9g0x7yL77K_txZqmwKo9bzINIhe6cR9ntdENjzRtQF02Qe54KgdcQQcdsadQwyBvTIkikBJfqyiAPyT6kwJDgDAxTjogc47XTfUXLt2NfNoVfv0H7ymLgSaY4cGDuZutwrnJgyU5wSMSv9emwcHXpxllw62tD2asX7Oi-L4WJD00q96DVL6OeMk7gQWzI_pS-eHohTFwVheNz_RYwhuzMNqhgQ_SqpdDgPKA'

// HTTP客户端类
class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json, text/plain, */*',
    };
  }

  // 设置授权token
  setAuthToken(token: string) {
    this.defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  // 通用请求方法
  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.defaultHeaders,
        ...options.headers,
        'Authorization': `Bearer ${token}`
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw new Error(`HTTP错误: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API请求失败:', error);
      throw error;
    }
  }

  // GET请求
  async get<T>(endpoint: string, params?: Record<string, unknown>): Promise<ApiResponse<T>> {
    let url = endpoint;
    
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      url += `?${searchParams.toString()}`;
    }
    
    return this.request<T>(url, { method: 'GET' });
  }

  // POST请求
  async post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

// 创建API客户端实例
export const apiClient = new ApiClient(API_CONFIG.baseURL);

// 业务API服务
export const searchToolApi = {
  // 执行搜索工具查询
  executeSearch: async (params: SearchToolParams): Promise<ApiResponse<SearchToolResponse>> => {
    return apiClient.get<SearchToolResponse>(API_CONFIG.endpoints.searchTool, {
      page_size: params.page_size || 50,
      page_no: params.page_no || 1,
      search_id: params.search_id,
      order: params.order || ''
    });
  }
};

// 工厂相关API
export const factoryApi = {
  // 获取出入库数据
  getInventoryData: async (searchId: string, pageSize = 50, pageNo = 1) => {
    return searchToolApi.executeSearch({
      search_id: searchId,
      page_size: pageSize,
      page_no: pageNo
    });
  }
};

//spu排产日历API
export const spuCalendarApi = {
  getSkuCalendarData: async (searchId: string, pageSize = 50, pageNo = 1) => {
    return searchToolApi.executeSearch({
      search_id: searchId,
      page_size: pageSize,
      page_no: pageNo
    });
  }
};

// 初始化API客户端 - 设置默认token
export const initializeApi = (token: string) => {
  apiClient.setAuthToken(token);
}; 